// Google Drive Validation Service
// Purpose: Validate Drive links and file structure for creative submissions
// Following existing service patterns with caching and error handling

import { supabase } from "./supabase";

// Cache for validation results (shorter duration since Drive content can change)
const cache = new Map();
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

const getCachedValidation = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`📋 Drive validation cache hit for ${key}`);
    return cached.data;
  }
  return null;
};

const setCachedValidation = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
  console.log(`💾 Drive validation cached for ${key}`);
};

class DriveValidationService {
  // ====================
  // LINK VALIDATION
  // ====================

  /**
   * Validate Google Drive link format and accessibility
   */
  async validateDriveLink(driveLink) {
    try {
      const cacheKey = `drive_link_${driveLink}`;
      const cachedResult = getCachedValidation(cacheKey);
      if (cachedResult) return cachedResult;

      console.log(`🚀 Validating Drive link: ${driveLink}`);

      // Basic URL format validation
      const linkValidation = this.validateLinkFormat(driveLink);

      if (!linkValidation.isValid) {
        return linkValidation;
      }

      // Extract Drive ID from various Google Drive URL formats
      const driveId = this.extractDriveId(driveLink);

      if (!driveId) {
        return {
          isValid: false,
          error: "Could not extract Google Drive ID from URL",
          details: "Please ensure you're using a valid Google Drive share link",
        };
      }

      // Check if link is accessible (basic check)
      const accessCheck = await this.checkDriveAccess(driveId);

      const result = {
        isValid: accessCheck.isAccessible,
        driveId: driveId,
        error: accessCheck.error,
        details: accessCheck.details,
        shareType: this.detectShareType(driveLink),
      };

      // Cache the result
      setCachedValidation(cacheKey, result);

      console.log(`✅ Drive link validation completed:`, {
        valid: result.isValid,
        driveId: result.driveId,
      });

      return result;
    } catch (err) {
      console.error("❌ DriveValidationService.validateDriveLink error:", err);
      return {
        isValid: false,
        error: "Validation failed",
        details: err.message,
      };
    }
  }

  /**
   * Validate basic Google Drive URL format
   */
  validateLinkFormat(url) {
    try {
      // Check if it's a valid URL
      const urlObj = new URL(url);

      // Check if it's a Google domain
      if (
        !urlObj.hostname.includes("drive.google.com") &&
        !urlObj.hostname.includes("docs.google.com")
      ) {
        return {
          isValid: false,
          error: "Invalid domain",
          details: "Link must be from drive.google.com or docs.google.com",
        };
      }

      // Check for common Google Drive URL patterns
      const validPatterns = [
        /drive\.google\.com\/drive\/folders\//,
        /drive\.google\.com\/file\/d\//,
        /drive\.google\.com\/open\?id=/,
        /docs\.google\.com\/document\/d\//,
        /docs\.google\.com\/spreadsheets\/d\//,
      ];

      const isValidPattern = validPatterns.some((pattern) => pattern.test(url));

      if (!isValidPattern) {
        return {
          isValid: false,
          error: "Invalid Google Drive URL format",
          details: "Please use a proper Google Drive share link",
        };
      }

      return {
        isValid: true,
        error: null,
        details: "URL format is valid",
      };
    } catch (err) {
      return {
        isValid: false,
        error: "Invalid URL",
        details: "Please provide a valid URL",
      };
    }
  }

  /**
   * Extract Google Drive ID from various URL formats
   */
  extractDriveId(url) {
    try {
      // Pattern 1: /folders/ID or /file/d/ID
      const folderMatch = url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
      if (folderMatch) return folderMatch[1];

      const fileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      if (fileMatch) return fileMatch[1];

      // Pattern 2: ?id=ID
      const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (idMatch) return idMatch[1];

      // Pattern 3: /d/ID (docs, sheets, etc.)
      const docMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
      if (docMatch) return docMatch[1];

      return null;
    } catch (err) {
      console.error("❌ Error extracting Drive ID:", err);
      return null;
    }
  }

  /**
   * Detect share type from URL
   */
  detectShareType(url) {
    if (url.includes("/folders/")) return "folder";
    if (url.includes("/file/d/")) return "file";
    if (url.includes("/document/d/")) return "document";
    if (url.includes("/spreadsheets/d/")) return "spreadsheet";
    return "unknown";
  }

  /**
   * Basic accessibility check for Google Drive link
   * Note: This is a simplified check - full validation would require Google Drive API
   */
  async checkDriveAccess(driveId) {
    try {
      // For now, we'll do basic validation
      // In production, this would use Google Drive API to check actual access

      if (!driveId || driveId.length < 10) {
        return {
          isAccessible: false,
          error: "Invalid Drive ID",
          details: "Drive ID appears to be malformed",
        };
      }

      // Simulate basic accessibility check
      // In real implementation, this would make an API call to Google Drive

      return {
        isAccessible: true,
        error: null,
        details: "Link appears to be valid (basic check only)",
      };
    } catch (err) {
      console.error("❌ Drive access check error:", err);
      return {
        isAccessible: false,
        error: "Access check failed",
        details: err.message,
      };
    }
  }

  // ====================
  // FILE STRUCTURE VALIDATION
  // ====================

  /**
   * Validate file structure based on requirements
   * Note: This is a template - full implementation would require Google Drive API
   */
  async validateFileStructure(driveLink, brand, affiliateName) {
    try {
      console.log(
        `🚀 Validating file structure for ${affiliateName}, brand: ${brand}`
      );

      const requiredStructure = this.getRequiredStructure(brand, affiliateName);

      // For now, return validation template
      // Full implementation would scan the Drive folder structure

      const validation = {
        isValid: true, // Would be determined by actual folder scanning
        requiredStructure,
        foundStructure: null, // Would be populated by Drive API scan
        issues: [],
        warnings: [],
        recommendations: [],
      };

      // Add basic validation rules
      validation.issues = this.validateBasicRequirements(
        driveLink,
        brand,
        affiliateName
      );
      validation.isValid = validation.issues.length === 0;

      console.log(`✅ File structure validation completed:`, {
        valid: validation.isValid,
        issues: validation.issues.length,
      });

      return validation;
    } catch (err) {
      console.error(
        "❌ DriveValidationService.validateFileStructure error:",
        err
      );
      return {
        isValid: false,
        error: "Structure validation failed",
        details: err.message,
        requiredStructure: this.getRequiredStructure(brand, affiliateName),
      };
    }
  }

  /**
   * Get required folder structure for brand and affiliate
   */
  getRequiredStructure(brand, affiliateName) {
    const brandName =
      brand === "holistic_hercules" ? "HolisticHercules" : "Aphrodite";

    return {
      mainFolder: `${brandName}_Creatives_${affiliateName}`,
      subfolders: {
        Images: {
          required: true,
          description: "Folder for image creatives",
          dateFolders: true, // Should contain M/D/YY date folders
        },
        Videos: {
          required: true,
          description: "Folder for video creatives",
          dateFolders: true, // Should contain M/D/YY date folders
        },
      },
      fileNaming: {
        pattern: "Name.Brand.Product.Number",
        examples: [
          "JohnDoe.HolisticHercules.TestBooster.001.jpg",
          "JaneSmith.Aphrodite.WeightLoss.045.mov",
        ],
      },
      requirements: [
        "Main folder must be named exactly: " +
          `${brandName}_Creatives_${affiliateName}`,
        "Must contain both Images and Videos subfolders",
        "Each subfolder must contain date folders in M/D/YY format",
        "Files must follow naming convention: Name.Brand.Product.Number",
        "Numbers must be unique and sequential for each product",
        'Sharing must be set to "Anyone with the link can view"',
      ],
    };
  }

  /**
   * Validate basic requirements (without API access)
   */
  validateBasicRequirements(driveLink, brand, affiliateName) {
    const issues = [];

    // Check if it's a folder link (for main submission)
    if (!driveLink.includes("/folders/")) {
      issues.push({
        type: "error",
        message: "Link must be to a Google Drive folder, not a file",
        solution: 'Right-click your folder and select "Get link"',
      });
    }

    // Check for sharing permissions hint in URL
    if (driveLink.includes("usp=sharing")) {
      // This is a good sign - means it was shared properly
    } else {
      issues.push({
        type: "warning",
        message: "Link may not have proper sharing permissions",
        solution: 'Ensure sharing is set to "Anyone with the link can view"',
      });
    }

    return issues;
  }

  // ====================
  // BUSINESS RULES VALIDATION
  // ====================

  /**
   * Check if submission meets business rules (timing, limits, etc.)
   */
  async validateSubmissionRules(affiliateId, brand, driveLink) {
    try {
      console.log(
        `🚀 Validating submission rules for affiliate ${affiliateId}, brand ${brand}`
      );

      const validation = {
        canSubmit: true,
        issues: [],
        warnings: [],
      };

      // Check daily submission limit
      const dailyStatus = await this.checkDailySubmissionLimit(
        affiliateId,
        brand
      );
      if (!dailyStatus.canSubmit) {
        validation.canSubmit = false;
        validation.issues.push({
          type: "error",
          message: `Daily submission limit reached (${dailyStatus.count}/${dailyStatus.max})`,
          solution: "You can submit again tomorrow after midnight EST",
        });
      }

      // Check deadline
      const deadlineStatus = this.checkSubmissionDeadline();
      if (!deadlineStatus.canSubmit) {
        validation.canSubmit = false;
        validation.issues.push({
          type: "error",
          message: deadlineStatus.message,
          solution: "Submit before 10:00 PM EST daily",
        });
      }

      // Check duplicate submissions
      const duplicateCheck = await this.checkDuplicateSubmission(
        affiliateId,
        brand,
        driveLink
      );
      if (duplicateCheck.isDuplicate) {
        validation.canSubmit = false;
        validation.issues.push({
          type: "error",
          message: "This Drive link has already been submitted",
          solution:
            "Use a different Drive link or update your existing submission",
        });
      }

      console.log(`✅ Submission rules validation completed:`, {
        canSubmit: validation.canSubmit,
        issues: validation.issues.length,
      });

      return validation;
    } catch (err) {
      console.error(
        "❌ DriveValidationService.validateSubmissionRules error:",
        err
      );
      return {
        canSubmit: false,
        issues: [
          {
            type: "error",
            message: "Validation failed",
            solution: "Please try again or contact support",
          },
        ],
        warnings: [],
      };
    }
  }

  /**
   * Check daily submission limit
   */
  async checkDailySubmissionLimit(affiliateId, brand) {
    try {
      const today = new Date().toISOString().split("T")[0];

      const { data, error } = await supabase
        .from("daily_submissions")
        .select("id")
        .eq("affiliate_id", affiliateId)
        .eq("brand", brand)
        .eq("submission_date", today);

      if (error) {
        console.error("❌ Daily limit check error:", error);
        return { canSubmit: true, count: 0, max: 1 }; // Fail open
      }

      const count = data?.length || 0;
      const max = 1; // Maximum submissions per day

      return {
        canSubmit: count < max,
        count,
        max,
      };
    } catch (err) {
      console.error("❌ Daily limit check error:", err);
      return { canSubmit: true, count: 0, max: 1 }; // Fail open
    }
  }

  /**
   * Check if within submission deadline (10 PM EST)
   */
  checkSubmissionDeadline() {
    try {
      const now = new Date();
      const estTime = new Date(
        now.toLocaleString("en-US", { timeZone: "America/New_York" })
      );
      const hour = estTime.getHours();

      // Deadline is 10 PM (22:00) EST
      const deadline = 22;

      if (hour >= deadline) {
        return {
          canSubmit: false,
          message:
            "Submissions are closed for today. Deadline is 10:00 PM EST.",
          nextDeadline: "Tomorrow at 10:00 PM EST",
        };
      }

      return {
        canSubmit: true,
        message: "Within submission hours",
        timeRemaining: `${deadline - hour} hours until deadline`,
      };
    } catch (err) {
      console.error("❌ Deadline check error:", err);
      return { canSubmit: true, message: "Deadline check failed" }; // Fail open
    }
  }

  /**
   * Check for duplicate submissions
   */
  async checkDuplicateSubmission(affiliateId, brand, driveLink) {
    try {
      const { data, error } = await supabase
        .from("daily_submissions")
        .select("id, submission_date")
        .eq("affiliate_id", affiliateId)
        .eq("brand", brand)
        .eq("drive_link", driveLink)
        .order("submitted_at", { ascending: false })
        .limit(1);

      if (error) {
        console.error("❌ Duplicate check error:", error);
        return { isDuplicate: false }; // Fail open
      }

      const isDuplicate = data && data.length > 0;

      return {
        isDuplicate,
        lastSubmission: isDuplicate ? data[0] : null,
      };
    } catch (err) {
      console.error("❌ Duplicate check error:", err);
      return { isDuplicate: false }; // Fail open
    }
  }

  // ====================
  // UTILITY METHODS
  // ====================

  /**
   * Get helpful validation tips for affiliates
   */
  getValidationTips() {
    return {
      driveLink: [
        'Use the "Get link" option from right-clicking your folder',
        'Set sharing to "Anyone with the link can view"',
        "Make sure it's a folder link, not a file link",
        "Test the link in an incognito window to verify access",
      ],
      fileStructure: [
        "Create main folder: Brand_Creatives_YourName",
        "Add Images and Videos subfolders",
        "Create date folders inside each (M/D/YY format)",
        "Name files: Name.Brand.Product.Number",
        "Keep sequential numbering for each product",
      ],
      submission: [
        "Submit before 10:00 PM EST daily",
        "One Drive link per brand per day maximum",
        "Include brief message about submission",
        "Wait for thumbs up reaction = launched",
      ],
    };
  }

  /**
   * Clear validation caches
   */
  clearCache() {
    cache.clear();
    console.log("🧹 Drive validation cache cleared");
  }
}

// Create and export singleton instance
const driveValidationService = new DriveValidationService();
export { driveValidationService };

// Export individual methods for convenience
export const {
  validateDriveLink,
  validateFileStructure,
  validateSubmissionRules,
  getValidationTips,
  clearCache,
} = driveValidationService;
